import { useState, useRef } from 'react';
import { useFileParser } from '../hooks/useFileParser';
import DropZone from './DropZone/DropZone';
import MetadataForm from './MetadataForm/MetadataForm';
import type { TextAnalysisParams, ParsedText, WordList } from '../types';

const SUPPORTED_TYPES = {
  'text/plain': 'txt',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'doc',
  'application/epub+zip': 'epub',
};

/**
 * Parse filename to extract title and author.
 * Handles formats like:
 * - "Title_by_Author.pdf" → { title: "Title", author: "Author" }
 * - "Title-by-Author.txt" → { title: "Title", author: "Author" }
 * - "Some_Random_Filename.pdf" → { title: "Some Random Filename", author: "" }
 */
function parseFilename(filename: string): { title: string; author: string } {
  // Remove file extension
  const name = filename.replace(/\.[^.]+$/, '');

  // Try to match "X by Y" pattern (case insensitive)
  const byMatch = name.match(/^(.+?)[-_\s]+by[-_\s]+(.+)$/i);

  if (byMatch) {
    const title = byMatch[1].replace(/[-_]+/g, ' ').trim();
    const author = byMatch[2].replace(/[-_]+/g, ' ').trim();
    return { title, author };
  }

  // No "by" pattern found - just clean up the filename for title
  const cleanTitle = name.replace(/[-_]+/g, ' ').trim();
  return { title: cleanTitle, author: '' };
}

/**
 * Apply metadata from parsed file using fallback chain:
 * 1. Extracted metadata (from PDF/EPUB/DOCX)
 * 2. Filename parsing
 * 3. Keep existing value
 */
function applyMetadataWithFallback(options: {
  parsed: ParsedText;
  filename: string;
  currentTitle: string;
  currentAuthor: string;
  currentSubject: string;
  setTitle: (title: string) => void;
  setAuthor: (author: string) => void;
  setSubject: (subject: string) => void;
}): void {
  const {
    parsed,
    filename,
    currentTitle,
    currentAuthor,
    currentSubject,
    setTitle,
    setAuthor,
    setSubject,
  } = options;

  // Apply title
  if (!currentTitle) {
    if (parsed.title) {
      setTitle(parsed.title);
    } else {
      const filenameParsed = parseFilename(filename);
      setTitle(filenameParsed.title);
    }
  }

  // Apply author
  if (!currentAuthor) {
    if (parsed.author) {
      setAuthor(parsed.author);
    } else {
      const filenameParsed = parseFilename(filename);
      if (filenameParsed.author) {
        setAuthor(filenameParsed.author);
      }
    }
  }

  // Apply subject if metadata provides it and user hasn't changed default
  if (parsed.subject && currentSubject === 'English Literature') {
    setSubject(parsed.subject);
  }
}

interface FileUploadProps {
  onTextReady: (params: TextAnalysisParams) => void;
  analyzing: boolean;
  savedList?: WordList;
}

export default function FileUpload({ onTextReady, savedList }: FileUploadProps) {
  const { parse } = useFileParser();

  const [inputMode, setInputMode] = useState<'file' | 'url' | 'paste'>('file');
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState<string>('');
  const [pasteText, setPasteText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [parseProgress, setParseProgress] = useState<string>('');

  // Metadata
  const [title, setTitle] = useState<string>(savedList?.title || '');
  const [author, setAuthor] = useState<string>(savedList?.author || '');
  const [gradeLevel, setGradeLevel] = useState<string>(savedList?.gradeLevel || '6-8');
  const [subject, setSubject] = useState<string>(savedList?.subject || 'English Literature');
  const [wordCount, setWordCount] = useState<number>(20);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (selectedFile: File | null) => {
    if (!selectedFile) return;
    const isSupported =
      Object.keys(SUPPORTED_TYPES).includes(selectedFile.type) ||
      /\.(txt|pdf|docx?|epub|md)$/i.test(selectedFile.name);
    if (!isSupported) {
      setError('Unsupported file type. Please upload PDF, DOCX, EPUB, or TXT.');
      return;
    }
    setFile(selectedFile);
    setError(null);

    // Don't auto-extract from filename here - wait for parser to extract metadata first
  };

  const handleFileMode = async (): Promise<ParsedText> => {
    if (!file) throw new Error('Please select a file.');

    setParseProgress(`Parsing ${file.name}...`);
    const parsed = await parse(file);

    // Apply metadata using fallback chain
    applyMetadataWithFallback({
      parsed,
      filename: file.name,
      currentTitle: title,
      currentAuthor: author,
      currentSubject: subject,
      setTitle,
      setAuthor,
      setSubject,
    });

    return parsed;
  };

  const handleUrlMode = async (): Promise<ParsedText> => {
    if (!url.trim()) throw new Error('Please enter a URL.');

    setParseProgress('Fetching URL content...');
    const parsed = await parse(url.trim());

    if (!title && parsed.title) setTitle(parsed.title);

    return parsed;
  };

  const handlePasteMode = (): ParsedText => {
    if (pasteText.trim().length < 100) {
      throw new Error('Please paste at least 100 characters of text.');
    }
    return { text: pasteText.trim(), title };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let parsed: ParsedText;

      if (inputMode === 'file') {
        parsed = await handleFileMode();
      } else if (inputMode === 'url') {
        parsed = await handleUrlMode();
      } else {
        parsed = handlePasteMode();
      }

      setParseProgress('');
      onTextReady({
        text: parsed.text,
        title: title || parsed.title || 'Untitled',
        author: author || parsed.author || '',
        gradeLevel,
        subject: subject || parsed.subject || 'English Literature',
        wordCount,
      });
    } catch (err) {
      setError((err as Error).message || 'Failed to process input');
      setParseProgress('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="upload-container">
      <h2>Upload Text</h2>

      {/* Input mode tabs */}
      <div className="tab-group" role="tablist">
        {(['file', 'url', 'paste'] as const).map((mode) => (
          <button
            key={mode}
            role="tab"
            aria-selected={inputMode === mode}
            className={`tab ${inputMode === mode ? 'tab-active' : ''}`}
            onClick={() => {
              setInputMode(mode);
              setError(null);
            }}
          >
            {{ file: 'Upload File', url: 'Paste URL', paste: 'Paste Text' }[mode]}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="upload-form">
        {/* File input */}
        {inputMode === 'file' && (
          <DropZone
            file={file}
            onFileSelect={handleFileSelect}
            onRemove={() => setFile(null)}
            fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
          />
        )}

        {/* URL input */}
        {inputMode === 'url' && (
          <div className="form-group">
            <label htmlFor="url-input">URL</label>
            <input
              id="url-input"
              type="url"
              value={url}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
              placeholder="https://example.com/article"
              className="form-input"
            />
            <small className="form-hint">
              Works best with articles and blog posts. JavaScript-rendered sites may not extract
              well.
            </small>
          </div>
        )}

        {/* Paste text */}
        {inputMode === 'paste' && (
          <div className="form-group">
            <label htmlFor="paste-input">Text</label>
            <textarea
              id="paste-input"
              value={pasteText}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPasteText(e.target.value)}
              placeholder="Paste the text you want to analyze here..."
              className="form-textarea"
              rows={10}
            />
            <small className="form-hint">
              {pasteText.length.toLocaleString()} characters
            </small>
          </div>
        )}

        {/* Metadata fields */}
        <MetadataForm
          title={title}
          onTitleChange={setTitle}
          author={author}
          onAuthorChange={setAuthor}
          gradeLevel={gradeLevel}
          onGradeLevelChange={setGradeLevel}
          subject={subject}
          onSubjectChange={setSubject}
          wordCount={wordCount}
          onWordCountChange={setWordCount}
        />

        {error && (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        )}

        {parseProgress && <div className="alert alert-info">{parseProgress}</div>}

        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={loading}
        >
          {loading ? 'Analyzing...' : 'Extract Vocabulary'}
        </button>
      </form>
    </div>
  );
}
